/**
 * Secure Access → Security — every security-relevant event, with export
 * (design 19 §7.6, VSNP-1223).
 *
 * Three things on one timeline, because an investigation needs all three:
 *
 *  1. the EVENTS the gateway refused (deny, session reject, posture reject,
 *     connector refusal, spoof drop, bundle rejection) — and the flows it
 *     held for classification that ended with no decision (flow.abandon,
 *     VSNP-1427) — from the audit stream;
 *  2. the SHAPE of them — denies over time by reason, and who/what is being
 *     denied — from the metrics plane and the daily roll-up;
 *  3. the ADMIN CHANGES from `activity_log`, so "the deny rate rose at 14:10"
 *     sits next to "someone changed the policy at 14:08".
 *
 * Export writes an `activity_log` row server-side (§7.6): an audit trail that
 * can be extracted with no record of the extraction has a hole exactly where
 * "who took a copy of this" belongs.
 */

import { useCallback, useEffect, useState } from 'react';
import { Clock, Download, KeyRound, RefreshCw, ShieldAlert } from 'lucide-react';
import ZtnaChip from '../../components/access/ZtnaChip';
import { AuditEventsTable } from '../../components/observability/AuditEventsTable';
import { QueryChart, QueryStat, QueryTable } from '../../components/observability/QueryPanels';
import { TimeRange } from '../../components/observability/TimeRange';
import { useToast } from '../../components/common/ToastContainer';
import accessObservabilityService, { type AccessActivityRow } from '../../services/accessObservability.service';
import accessService, { type DeviceSubjectMode, type SignInLevel, type TenantSignInPolicy } from '../../services/access.service';
import SignInLifetimeFields from '../../components/access/SignInLifetimeFields';
import { FALLBACK_SIGN_IN_LIMITS, defaultsAsEffective, describeValue, signInLevelError } from '../../utils/signInPolicy';
import { useAuthStore } from '../../stores/authStore';
import { USER_TYPES } from '../../utils/constants';
import activityService, { type ActivityRecord } from '../../services/activity.service';
import { describeSignInRoutingActivity } from '../../utils/signInRouting';
import { useTenantStore } from '../../stores/tenantStore';
import { SECURITY_EVENT_UNION, fmtBytes } from '../../utils/obsChart';
import {
  DEFAULT_TIME_RANGE,
  rangeDuration,
  rangeLabel,
  resolveTimeRange,
  type TimeRangeValue,
} from '../../utils/timeRange';

/**
 * Admin actions worth putting on a security timeline.
 *
 * Sent to the SERVER as `actionPrefix` (doc 22 §10.3). Filtering here was the
 * bug: the endpoint returned the 100 newest rows of EVERY action and the page
 * kept the access ones, so on a busy tenant the relevant rows were paged out
 * before the filter ran and this panel showed nothing while the changes
 * existed — and an empty security panel reads as "nothing happened".
 */
const ADMIN_ACTION_PREFIXES = ['access_', 'policy', 'device', 'gateway', 'connector', 'posture'];

/** The event names the table above unions — and therefore what an export must cover. */
const EXPORT_EVENTS = [...SECURITY_EVENT_UNION];

const AccessSecurity = () => {
  const toast = useToast();
  const [range, setRange] = useState<TimeRangeValue>(DEFAULT_TIME_RANGE);
  const [nonce, setNonce] = useState(0);
  const [exporting, setExporting] = useState<'csv' | 'jsonl' | null>(null);
  const [exportNote, setExportNote] = useState<string | null>(null);

  const [topDenied, setTopDenied] = useState<{
    users: AccessActivityRow[];
    devices: AccessActivityRow[];
    apps: AccessActivityRow[];
    hosts: AccessActivityRow[];
  }>({ users: [], devices: [], apps: [], hosts: [] });
  const [admin, setAdmin] = useState<ActivityRecord[]>([]);
  const [deviceOnly, setDeviceOnly] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  // Device→subject enforcement (WP8B, design 23 §3.1). Read on mount and
  // flipped from here, because this is the page where the evidence for the
  // flip lives: an operator decides to enforce by looking at the token
  // binding counts immediately below, and a control that made them navigate
  // away to act on what they just read would be the wrong shape.
  const currentUser = useAuthStore((s) => s.user);
  const canSetBinding =
    currentUser?.type === USER_TYPES.SUPER_ADMIN || currentUser?.type === USER_TYPES.TENANT_ADMIN;
  const [subjectMode, setSubjectMode] = useState<DeviceSubjectMode | null>(null);
  const [savingMode, setSavingMode] = useState(false);

  // The tenant's sign-in lifetime policy (doc 28 §4.10): how often a Secure
  // Access user signs in again, and how long each access token lives. Device
  // groups and VPN users can override it; this is the tenant level. Saving
  // recompiles the signed bundle — the gateways enforce it from there.
  const [signIn, setSignIn] = useState<TenantSignInPolicy | null>(null);
  const [signInDraft, setSignInDraft] = useState<SignInLevel>({ reauthInterval: null, tokenLifetime: null });
  const [savingSignIn, setSavingSignIn] = useState(false);
  // Bumped when the draft is reset from outside the fields (save, cancel), so
  // their "custom…" choice is re-derived from the value.
  const [signInEpoch, setSignInEpoch] = useState(0);

  // Every window on this page follows the picker (doc 22 §10.4). `win` is the
  // rate window (a fraction of the range, so a chart stays smooth); `total` is
  // the whole range, for counters.
  const resolved = resolveTimeRange(range);
  const win = resolved.window;
  const total = rangeDuration(resolved);
  const label = rangeLabel(range);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { fromIso, toIso } = resolveTimeRange(range);
      const day = { from: fromIso.slice(0, 10), to: toIso.slice(0, 10) };
      const tenantId = useTenantStore.getState().getTenantId();
      const [users, devices, apps, hosts, activity, sessions] = await Promise.all([
        accessObservabilityService.getActivity({ ...day, groupBy: 'user', rankBy: 'denies', limit: 5 }, { showLoading: false }),
        accessObservabilityService.getActivity({ ...day, groupBy: 'device', rankBy: 'denies', limit: 5 }, { showLoading: false }),
        accessObservabilityService.getActivity({ ...day, groupBy: 'app', rankBy: 'denies', limit: 5 }, { showLoading: false }),
        // "Top denied hosts" (design §9b): the app name answers "what was
        // refused" only for registered apps, and the interesting denies are
        // usually breakout traffic whose whole identity is its hostname.
        accessObservabilityService.getActivity({ ...day, groupBy: 'host', rankBy: 'denies', limit: 5 }, { showLoading: false }),
        // `/activity` is excluded from the api.ts tenant injection AND the
        // service returns an empty page to a SuperAdmin who names no tenant
        // (an unscoped scan of activity_log once OOM-killed MySQL). So the
        // tenant and the window are passed explicitly here — without the
        // tenant this panel is silently always empty.
        activityService
          .list({
            page: 1,
            pageSize: 100,
            tenantId: tenantId || undefined,
            dateFrom: fromIso,
            dateTo: toIso,
            actionPrefix: ADMIN_ACTION_PREFIXES,
          })
          .catch(() => ({ items: [] as ActivityRecord[], pagination: { page: 1, pageSize: 0, total: 0, totalPages: 0 } })),
        // The device-only subject set for the rankings, for the same reason the
        // Activity page needs it: the roll-up has no idp_verified column.
        accessObservabilityService
          .listSessions({ from: fromIso, to: toIso, limit: 500 }, { showLoading: false })
          .catch(() => ({ sessions: [], total: 0, limit: 0, offset: 0 })),
      ]);
      setTopDenied({
        users: (users?.rows ?? []).filter((r) => r.denies > 0),
        devices: (devices?.rows ?? []).filter((r) => r.denies > 0),
        apps: (apps?.rows ?? []).filter((r) => r.denies > 0),
        hosts: (hosts?.rows ?? []).filter((r) => r.denies > 0),
      });
      // No client-side action filter: the server now applies the prefixes, so
      // what arrives is already the 100 newest RELEVANT rows rather than the
      // 100 newest of everything.
      setAdmin(activity?.items ?? []);
      const cns = new Set<string>();
      for (const s of sessions?.sessions ?? []) {
        if (!s.idpVerified && s.user) cns.add(s.user);
      }
      setDeviceOnly(cns);
    } catch {
      setTopDenied({ users: [], devices: [], apps: [], hosts: [] });
      setAdmin([]);
      setDeviceOnly(new Set());
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void load();
  }, [load]);

  // Read once per tenant selection, not on the time-range picker: the mode is
  // tenant configuration, not a windowed measurement, and re-reading it on
  // every range change would make the toggle flicker while an operator is
  // reading the counts beside it.
  const tenantKey = useTenantStore((s) => s.selectedTenantId);
  useEffect(() => {
    let cancelled = false;
    accessService
      .getDeviceBinding()
      .then((r) => {
        if (!cancelled) setSubjectMode(r.subjectMode);
      })
      // Left as null on failure, which renders "unknown" rather than
      // "audit" — claiming a security control is in its safe default when we
      // could not read it is the one answer that must not be guessed.
      .catch(() => {
        if (!cancelled) setSubjectMode(null);
      });
    accessService
      .getSignInPolicy()
      .then((r) => {
        if (cancelled) return;
        setSignIn(r);
        setSignInDraft(r.tenant);
      })
      // Left null: "unknown", not the defaults — the same rule as the mode.
      .catch(() => {
        if (!cancelled) setSignIn(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantKey]);

  const signInDirty =
    !!signIn &&
    (signIn.tenant.reauthInterval !== signInDraft.reauthInterval || signIn.tenant.tokenLifetime !== signInDraft.tokenLifetime);

  const saveSignIn = async () => {
    if (!signIn) return;
    const problem = signInLevelError(signInDraft, signIn);
    if (problem) {
      toast.error('Invalid value', problem);
      return;
    }
    setSavingSignIn(true);
    try {
      const result = await accessService.setSignInPolicy({
        reauthInterval: signInDraft.reauthInterval,
        tokenLifetime: signInDraft.tokenLifetime,
      });
      setSignIn(result);
      setSignInDraft(result.tenant);
      setSignInEpoch((n) => n + 1);
      toast.success(
        'Sign-in policy saved',
        `Policy bundle ${result.compile.version} published to this tenant's gateways`,
      );
      setNonce((n) => n + 1);
    } catch (err) {
      toast.error('Could not save the sign-in policy', err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSavingSignIn(false);
    }
  };

  const toggleSubjectMode = async () => {
    if (!subjectMode) return;
    const next: DeviceSubjectMode = subjectMode === 'enforce' ? 'audit' : 'enforce';
    setSavingMode(true);
    try {
      const result = await accessService.setDeviceBinding(next);
      setSubjectMode(result.subjectMode);
      toast.success(
        next === 'enforce' ? 'Device subject enforcement ON' : 'Device subject enforcement in audit',
        `Policy bundle ${result.compile.version} published to this tenant's gateways`,
      );
      setNonce((n) => n + 1);
    } catch (err) {
      toast.error('Could not change the mode', err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSavingMode(false);
    }
  };

  const runExport = async (format: 'csv' | 'jsonl') => {
    setExporting(format);
    setExportNote(null);
    try {
      const { fromIso, toIso } = resolveTimeRange(range);
      const body = await accessObservabilityService.exportAudit({
        format,
        from: fromIso,
        to: toIso,
        // The SAME event union the table above shows (doc 22 §10.1) — seven
        // names since flow.abandon (VSNP-1427); the API accepts up to eight.
        // Exporting only flow.deny gave an auditor a subset of what was on
        // their screen without saying so — the one failure mode an export
        // format is supposed to rule out.
        events: EXPORT_EVENTS,
        limit: 10000,
      });
      const rows = body ? body.trimEnd().split('\n').length - (format === 'csv' ? 1 : 0) : 0;
      setExportNote(`${format.toUpperCase()}: ${rows} row${rows === 1 ? '' : 's'}, ${body.length} bytes — logged to the audit trail`);

      // Offer the file. The browser decides whether it may be saved; the export
      // itself has already happened and is already recorded server-side, so a
      // blocked download is a browser policy, not a failed export.
      const blob = new Blob([body], { type: format === 'csv' ? 'text/csv' : 'application/x-ndjson' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `access-audit-${fromIso.slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export ready', `${rows} rows — the extraction is recorded in the activity log`);
    } catch (err) {
      toast.error('Export failed', err instanceof Error ? err.message : 'Could not export the audit range');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="access-security">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-gray-900">Security</h1>
            <ZtnaChip />
          </div>
          <p className="mt-1 text-gray-600">
            Denies, rejections, refusals and abandoned (never-classified) flows from the tamper-evident audit
            stream, next to the admin changes that could explain them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TimeRange value={range} onChange={setRange} />
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              void load();
              // Keyed remount for the panels that fetch for themselves.
              setNonce((n) => n + 1);
            }}
          >
            <RefreshCw className={`mr-2 inline h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Events + facets + export (design §7.6) */}
      <AuditEventsTable
        key={`sec-${nonce}`}
        range={range}
        preset="security"
        showFacets
        limit={500}
        title="Security events"
        testId="security-events"
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-secondary px-2 py-1 text-xs"
              onClick={() => void runExport('csv')}
              disabled={exporting !== null}
              data-testid="export-csv"
            >
              <Download className="mr-1 inline h-3.5 w-3.5" />
              {exporting === 'csv' ? 'Exporting…' : 'CSV'}
            </button>
            <button
              type="button"
              className="btn-secondary px-2 py-1 text-xs"
              onClick={() => void runExport('jsonl')}
              disabled={exporting !== null}
              data-testid="export-jsonl"
            >
              <Download className="mr-1 inline h-3.5 w-3.5" />
              {exporting === 'jsonl' ? 'Exporting…' : 'JSONL'}
            </button>
          </div>
        }
      />
      {exportNote && (
        <p className="text-xs text-gray-500" data-testid="export-note">
          {exportNote}
        </p>
      )}

      {/* Device-bound tokens (WP8B, design 23 §3.1/§3.3).
          The control and the evidence for using it sit together: the counts on
          the right are exactly what says whether this tenant is ready to
          enforce, and `unbound` reaching zero for the supported client
          versions is the documented precondition for the flip. */}
      <div className="card grid grid-cols-1 gap-4 p-4 lg:grid-cols-2" data-testid="sec-device-binding">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <KeyRound className="h-4 w-4 text-gray-400" />
            Device-bound tokens
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            A session's token must name somebody this device belongs to. In{' '}
            <span className="font-medium">audit</span> a mismatch is recorded and the session is
            admitted; in <span className="font-medium">enforce</span> it is refused.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <span
              className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium ${
                subjectMode === 'enforce'
                  ? 'bg-teal-50 text-teal-700'
                  : subjectMode === 'audit'
                    ? 'bg-gray-100 text-gray-700'
                    : 'bg-warning-50 text-warning-700'
              }`}
              data-testid="sec-subject-mode"
            >
              {subjectMode ?? 'unknown'}
            </span>
            {canSetBinding && subjectMode && (
              <button
                type="button"
                className="btn-secondary px-2 py-1 text-xs"
                onClick={() => void toggleSubjectMode()}
                disabled={savingMode}
                data-testid="sec-subject-mode-toggle"
              >
                {savingMode
                  ? 'Publishing…'
                  : subjectMode === 'enforce'
                    ? 'Switch to audit'
                    : 'Switch to enforce'}
              </button>
            )}
          </div>
          {subjectMode === 'enforce' && (
            <p className="mt-2 text-xs text-warning-700">
              A device with no recorded owner cannot connect while this is on. Check the Devices page
              for rows reading <span className="font-medium">unassigned</span> first.
            </p>
          )}
        </div>
        <div className="space-y-3">
          {/* The bound ratio is THE rollout number (design 23 §3.3 step 4):
              enforcement is safe to turn on for the fleet once it reaches 100 %
              for the supported client versions. It is given its own figure
              rather than left to be worked out from the rows below, because a
              reader who has to do arithmetic to find the one number a decision
              depends on will read the rows instead and decide on the wrong one.

              The denominator is every outcome, so a session refused for a
              subject mismatch still counts against the ratio — it presented a
              token and that token was not bound. A ratio over `bound + unbound`
              alone would flatter a tenant whose ownership data is broken. */}
          <QueryStat
            key={`sec-bound-ratio-${nonce}-${total}`}
            title={`Tokens bound to their device (${label})`}
            query={
              `100 * (sum(increase(agw_token_binding_total{result="bound"}[${total}])) or vector(0))` +
              ` / sum(increase(agw_token_binding_total[${total}]))`
            }
            format={(v) => (v === null ? '—' : `${Math.round(v)}%`)}
            hint="100 % for every supported client version is the precondition for enforcing fleet-wide. '—' means no session presented a token in this range."
            testId="sec-bound-ratio"
          />
          <QueryTable
            key={`sec-binding-${nonce}-${total}`}
            title={`Token binding outcomes (${label})`}
            query={`sum by (result) (increase(agw_token_binding_total[${total}]))`}
            labelKey="result"
            valueLabel="sessions"
            format={(v) => String(Math.round(v))}
            emptyMessage={`No session presented a token in the last ${label}.`}
            testId="sec-token-binding"
          />
        </div>
      </div>

      {/* Sign-in lifetime (doc 28 §4.10). The tenant level; a device group's
          or a VPN user's own value wins over it. Enforced by the gateways from
          the token's auth_time (re-authentication) and iat (lifetime). */}
      <div className="card grid grid-cols-1 gap-4 p-4 lg:grid-cols-2" data-testid="sec-sign-in-policy">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Clock className="h-4 w-4 text-gray-400" />
            Sign-in lifetime
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            How often Secure Access users must type their password (or complete SSO) again, and how long each
            access token lives. A device group or a VPN user can set its own; the most specific wins. Admin
            portal sessions are not affected (Settings → Security → Session Management).
          </p>
          <p className="mt-2 text-xs text-gray-700" data-testid="sec-sign-in-current">
            {signIn
              ? <>Now: sign in again every <span className="font-medium">{describeValue(signIn.effective.reauthInterval, signIn.effective.reauthIntervalSource)}</span>
                {' · '}token lifetime <span className="font-medium">{describeValue(signIn.effective.tokenLifetime, signIn.effective.tokenLifetimeSource)}</span></>
              : 'unknown'}
          </p>
        </div>
        <div className="space-y-3">
          {signIn && (
            <SignInLifetimeFields
              key={`${signIn.tenantId}-${signInEpoch}`}
              value={signInDraft}
              onChange={setSignInDraft}
              inherited={defaultsAsEffective(signIn ?? FALLBACK_SIGN_IN_LIMITS)}
              level="tenant"
              limits={signIn}
              inheritLabel="Default"
              disabled={!canSetBinding || savingSignIn}
              testIdPrefix="sec"
            />
          )}
          {canSetBinding && signIn && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn-primary px-3 py-1 text-xs"
                onClick={() => void saveSignIn()}
                disabled={!signInDirty || savingSignIn}
                data-testid="sec-sign-in-save"
              >
                {savingSignIn ? 'Publishing…' : 'Save'}
              </button>
              {signInDirty && (
                <button
                  type="button"
                  className="btn-secondary px-3 py-1 text-xs"
                  onClick={() => {
                    setSignInDraft(signIn.tenant);
                    setSignInEpoch((n) => n + 1);
                  }}
                  disabled={savingSignIn}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Shape of the denies, each panel with the EVENTS behind it.
          A counter alone cannot distinguish "the metric is missing" from
          "nothing happened in this range" — both draw an empty box. Pairing
          every panel with its own audit-event list, and rendering an explicit
          "no events in range" when the query succeeds with no rows, makes the
          two readable apart (design §9b; brief WP7A item 5). */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <QueryChart
            key={`sec-c1-${nonce}-${win}`}
            title="Denies per second by reason"
            query={`sum by (reason) (rate(agw_flow_decisions_total{action="deny"}[${win}]))`}
            range={range}
            labelKeys={['reason']}
            testId="sec-chart-denies"
          />
          <QueryChart
            key={`sec-c3-${nonce}-${win}`}
            title="Posture updates (verified vs rejected)"
            query={`sum by (result) (rate(agw_posture_updates_total[${win}]))`}
            range={range}
            labelKeys={['result']}
            testId="sec-chart-posture"
          />
          <AuditEventsTable
            key={`sec-e-posture-${nonce}`}
            range={range}
            event="posture.reject"
            compact
            limit={50}
            title="Posture rejections"
            emptyMessage={`No posture document failed verification in the last ${label}.`}
            testId="sec-events-posture"
          />
        </div>

        <div className="space-y-4">
          <QueryChart
            key={`sec-c2-${nonce}-${win}`}
            title="Session rejections by reason"
            query={`sum by (reason) (rate(agw_sessions_rejected_total[${win}]))`}
            range={range}
            labelKeys={['reason']}
            testId="sec-chart-rejects"
          />
          <AuditEventsTable
            key={`sec-e-reject-${nonce}`}
            range={range}
            event="session.reject"
            compact
            limit={50}
            title="Session rejections"
            emptyMessage={`No session was turned away in the last ${label}.`}
            testId="sec-events-rejects"
          />
        </div>

        <div className="space-y-4">
          {/* The rarer signals. They are counters that should read zero, so a
              table of exact counts is more useful than a line at the axis. */}
          <QueryTable
            key={`sec-t1-${nonce}-${total}`}
            title={`Policy bundle applies (${label})`}
            query={`sum by (result) (increase(agw_policy_bundle_apply_total[${total}]))`}
            labelKey="result"
            valueLabel="count"
            format={(v) => String(Math.round(v))}
            emptyMessage={`No bundle applies in the last ${label}.`}
            testId="sec-bundle-applies"
          />
          <AuditEventsTable
            key={`sec-e-bundle-${nonce}`}
            range={range}
            event="policy.rejected"
            compact
            limit={50}
            title="Bundle rejections"
            emptyMessage={`No gateway refused a policy bundle in the last ${label}.`}
            testId="sec-events-bundle"
          />
        </div>

        <div className="space-y-4">
          <QueryTable
            key={`sec-t2-${nonce}-${total}`}
            title={`L3 spoof drops (${label})`}
            query={`sum by (node) (increase(agw_l3_spoof_drops_total[${total}]))`}
            labelKey="node"
            valueLabel="packets"
            format={(v) => String(Math.round(v))}
            emptyMessage="No inner packet claimed a source it had not been leased."
            testId="sec-spoof-drops"
          />
          <AuditEventsTable
            key={`sec-e-spoof-${nonce}`}
            range={range}
            event="l3.spoof_drop"
            compact
            limit={50}
            title="Spoof drops"
            emptyMessage={`No spoofed inner packet in the last ${label}.`}
            testId="sec-events-spoof"
          />
          <QueryTable
            key={`sec-t3-${nonce}-${total}`}
            title={`Connector claims refused (${label})`}
            query={`sum by (node) (increase(agw_connector_claims_refused_total[${total}]))`}
            labelKey="node"
            valueLabel="refusals"
            format={(v) => String(Math.round(v))}
            emptyMessage="No connector was refused a claim."
            testId="sec-connector-refusals"
          />
          <AuditEventsTable
            key={`sec-e-claims-${nonce}`}
            range={range}
            event="connector.refuse"
            compact
            limit={50}
            title="Connector claim refusals"
            emptyMessage={`No connector asked for an app it was not assigned in the last ${label}.`}
            testId="sec-events-claims"
          />
        </div>
      </div>

      {/* Top denied (roll-up) */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        {([
          ['Top denied users', topDenied.users, 'user'],
          ['Top denied devices', topDenied.devices, 'device'],
          ['Top denied apps', topDenied.apps, 'app'],
          ['Top denied hosts', topDenied.hosts, 'host'],
        ] as [string, AccessActivityRow[], string][]).map(([title, rows, kind]) => (
          <div key={kind} className="card" data-testid={`top-denied-${kind}`}>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900">
              <ShieldAlert className="h-4 w-4 text-danger-500" /> {title}
            </h3>
            {loading ? (
              <p className="text-sm text-gray-400">Loading…</p>
            ) : rows.length === 0 ? (
              <p className="text-sm text-gray-400">No denies in the rolled-up days for this range.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key}>
                      <td className="py-0.5 pr-2 text-gray-700">
                        {/* A "user" here can be a certificate CN on a
                            device-only session; the ranking must not present
                            one as a person (brief §3 WP4). */}
                        {kind === 'user' && deviceOnly.has(r.key) ? `device-only (${r.key})` : r.key || '—'}
                      </td>
                      <td className="py-0.5 text-right font-medium text-danger-700">{r.denies}</td>
                      <td className="py-0.5 pl-2 text-right text-xs text-gray-400">{fmtBytes(r.bytesTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>

      {/* Admin changes on the same timeline (design §7.6) */}
      <div className="card" data-testid="admin-changes">
        <h3 className="mb-2 text-sm font-semibold text-gray-900">
          Admin changes in this window
          <span className="ml-2 text-xs font-normal text-gray-400">from the platform activity log</span>
        </h3>
        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : admin.length === 0 ? (
          <p className="text-sm text-gray-400">No access-related admin actions in this window.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-3">Time</th>
                <th className="py-2 pr-3">Who</th>
                <th className="py-2 pr-3">Action</th>
                <th className="py-2 pr-3">Resource</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {admin.map((l) => (
                <tr key={l.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap py-1.5 pr-3 font-mono text-[11px] text-gray-500">
                    {(l.created_at ?? '').replace('T', ' ').slice(0, 19)}
                  </td>
                  <td className="py-1.5 pr-3 text-xs text-gray-700">{l.user_email ?? '—'}</td>
                  <td className="py-1.5 pr-3 text-xs font-medium text-gray-800">
                    {/* Identity-provider / sign-in-routing rows read as words (doc 28d §10.3). */}
                    {describeSignInRoutingActivity(l)?.label ?? l.action}
                    {describeSignInRoutingActivity(l) && (
                      <span className="block font-normal text-gray-500">{describeSignInRoutingActivity(l)?.summary}</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-xs text-gray-600">
                    {l.resource_name ?? l.resource_id ?? '—'}
                    {l.resource_type && <span className="ml-1 text-gray-400">({l.resource_type})</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AccessSecurity;
